import React, {useEffect, useState} from "react";
import {useResourcePersonBooks} from "../store/resourcePerson/resourcePersonStore";
import UploadField from "../components/resourcePerson/UploadField";
import {useConfirmDialog} from "../components/ConfirmDialogProvider";
import ResourcePersonNavBar from "../components/ResourcePersonNavBar";
import ResourceAnalyticsPanel from "../components/resourcePerson/ResourceAnalyticsPanel";
import ResourceDownloadLink from "../components/resourcePerson/ResourceDownloadLink";
import { RichTextField, cleanHtmlForSave } from "../components/resourcePerson/RichTextField";
import { StringListEditor, TableOfContentsEditor } from "../components/resourcePerson/MetadataEditors";
import {useNavigate, useParams} from "react-router-dom";
import {
  ACADEMIC_LEVELS,
  FILE_FORMATS,
  arrayToLines,
  linesToArray,
  parseTableOfContents,
  stringifyTableOfContents,
} from "../utils/resourceMetadata";

const EMPTY_FORM = {
  title: "",
  author: "",
  description: "",
  summary: "",
  abstract: "",
  chapterOnePreview: "",
  tableOfContents: "",
  whatIsIncluded: "",
  academicLevel: "",
  fieldOfStudy: "",
  fileFormat: "",
  price: "",
  coverImage: "",
  fileUrl: "",
  filePublicId: "",
  fileCloudType: "",
  fileDeliveryType: "",
};

const inputClass =
  "w-full rounded-lg border border-blue-100 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";
const STATUSES = ["", "DRAFT", "PENDING", "APPROVED", "REJECTED", "DISABLED", "WITHDRAWN"];

export default function BookUploadForm() {
  const {id: routeBookId} = useParams();
  const navigate = useNavigate();
  const store = useResourcePersonBooks();
  const confirm = useConfirmDialog();
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (routeBookId) return;
    store.fetchBooks({page, limit: 10, ...(search ? {search} : {}), ...(status ? {status} : {})});
  }, [page, search, status, routeBookId, store.fetchBooks]);

  useEffect(() => {
    if (!routeBookId) return;
    store.fetchBookById(routeBookId);
    store.fetchBookAnalytics(routeBookId);
    store.fetchBookDownloads(routeBookId, {page: 1, limit: 10});
  }, [routeBookId, store.fetchBookById, store.fetchBookAnalytics, store.fetchBookDownloads]);

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(false);
  };

  const editBook = (book) => {
    setEditingId(book.id || book._id);
    setForm({
      title: book.title || "",
      author: book.author || "",
      description: book.description || "",
      summary: book.summary || "",
      abstract: book.abstract || "",
      chapterOnePreview: book.chapterOnePreview || book.chapter_one_preview || "",
      tableOfContents: stringifyTableOfContents(book.tableOfContents || book.table_of_contents),
      whatIsIncluded: arrayToLines(book.whatIsIncluded || book.what_is_included),
      academicLevel: book.academicLevel || book.academic_level || "",
      fieldOfStudy: book.fieldOfStudy || book.field_of_study || "",
      fileFormat: book.fileFormat || book.file_format || "",
      price: book.price ?? "",
      coverImage: book.coverImage || book.cover_image || "",
      fileUrl: book.fileUrl || book.file_url || "",
      filePublicId: book.filePublicId || book.file_public_id || "",
      fileCloudType: book.fileCloudType || book.file_cloud_type || "",
      fileDeliveryType: book.fileDeliveryType || book.file_delivery_type || "",
    });
    setShowForm(true);
    setError("");
    setMessage("");
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    setError("");
    setMessage("");
  };

  const saveBook = async (submitForReview) => {
    setError("");
    setMessage("");
    if (!form.title.trim() || !form.author.trim() || !form.description.trim() || !form.fileUrl) {
      setError("Title, author, description, and the book file are required.");
      return;
    }

    let tableOfContents;
    try {
      tableOfContents = parseTableOfContents(form.tableOfContents);
    } catch (err) {
      setError(err.message);
      return;
    }

    const payload = {
      title: form.title.trim(),
      author: form.author.trim(),
      description: cleanHtmlForSave(form.description),
      ...(cleanHtmlForSave(form.summary) ? {summary: cleanHtmlForSave(form.summary)} : {}),
      ...(cleanHtmlForSave(form.abstract) ? {abstract: cleanHtmlForSave(form.abstract)} : {}),
      ...(cleanHtmlForSave(form.chapterOnePreview)
        ? {chapterOnePreview: cleanHtmlForSave(form.chapterOnePreview)}
        : {}),
      ...(tableOfContents.length ? {tableOfContents} : {}),
      ...(linesToArray(form.whatIsIncluded).length
        ? {whatIsIncluded: linesToArray(form.whatIsIncluded)}
        : {}),
      ...(form.academicLevel ? {academicLevel: form.academicLevel} : {}),
      ...(form.fieldOfStudy.trim() ? {fieldOfStudy: form.fieldOfStudy.trim()} : {}),
      ...(form.fileFormat ? {fileFormat: form.fileFormat} : {}),
      fileUrl: form.fileUrl,
      ...(form.filePublicId ? {filePublicId: form.filePublicId} : {}),
      ...(form.fileCloudType ? {fileCloudType: form.fileCloudType} : {}),
      ...(form.fileDeliveryType ? {fileDeliveryType: form.fileDeliveryType} : {}),
      price: form.price === "" ? 0 : Number(form.price),
      ...(form.coverImage ? {coverImage: form.coverImage} : {}),
    };
    const result = editingId
      ? await store.updateBook(editingId, payload)
      : await store.createBook(payload);
    if (!result.success) {
      setError(result.error || "Could not save the book.");
      return;
    }

    const book = result.book || {};
    const id = editingId || book.id || book._id;
    if (submitForReview) {
      if (!id) {
        setError("The book draft was saved, but the server did not return its ID, so it could not be submitted.");
        return;
      }
      const submission = await store.submitBook(id);
      if (!submission.success) {
        setError(`The draft was saved, but submission failed: ${submission.error || "Request failed"}`);
        store.fetchBooks({page, limit: 10, ...(search ? {search} : {}), ...(status ? {status} : {})});
        return;
      }
    }

    setMessage(submitForReview ? "Book submitted for review." : "Book draft saved.");
    resetForm();
    store.fetchBooks({page, limit: 10, ...(search ? {search} : {}), ...(status ? {status} : {})});
  };

  const runAction = async (action, successMessage, refreshSelected = true) => {
    setError("");
    setMessage("");
    const result = await action();
    if (!result.success) {
      setError(result.error || "Request failed.");
      return;
    }
    setMessage(successMessage);
    store.fetchBooks({page, limit: 10, ...(search ? {search} : {}), ...(status ? {status} : {})});
    if (routeBookId && refreshSelected) store.fetchBookById(routeBookId);
  };

  const isDraft = (book) => String(book?.status || "").toUpperCase() === "DRAFT";
  const isEditable = (book) =>
    ["DRAFT", "REJECTED"].includes(String(book?.status || "").toUpperCase());
  const detail = String(store.bookDetail?.id || store.bookDetail?._id || "") === String(routeBookId || "")
    ? store.bookDetail
    : null;
  const draftCount = store.books.filter(isDraft).length;
  const pendingCount = store.books.filter((book) => String(book.status || "").toUpperCase() === "PENDING").length;

  return (
    <div className="min-h-screen bg-stone-50 font-sans text-stone-900">
      <ResourcePersonNavBar title="Books" subTitle="Authoring" />
      <main className="mx-auto max-w-6xl space-y-6 px-5 pb-10 pt-4">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Resource library</p>
            <h1 className="text-3xl font-bold tracking-tight text-stone-900">
              {routeBookId ? (detail?.title || "Book details") : showForm ? (editingId ? "Edit book" : "Create a book") : "My Books"}
            </h1>
            <p className="mt-2 max-w-xl text-sm text-stone-500">
              {routeBookId
                ? "Book information, reader activity and performance."
                : showForm
                ? "Add the book details and files, then save to return to your collection."
                : "Create and manage your book collection, submit drafts for review, and follow reader activity."}
            </p>
          </div>
          {routeBookId ? (
            <button
              type="button"
              onClick={() => navigate("/resource-person/book-upload")}
              className="rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-bold text-stone-700 shadow-sm transition hover:bg-stone-50"
            >
              Back to books
            </button>
          ) : (
            <button
              type="button"
              onClick={showForm ? resetForm : openCreate}
              className="rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-800"
            >
              {showForm ? "Back to books" : "+ New book"}
            </button>
          )}
        </header>

        {!routeBookId && !showForm && <section className="grid gap-3 sm:grid-cols-3" aria-label="Book summary">
          {[
            {label: "Books found", value: store.booksTotal, tone: "text-blue-800", accent: "bg-blue-600"},
            {label: "Drafts on this page", value: draftCount, tone: "text-amber-700", accent: "bg-amber-500"},
            {label: "In review on this page", value: pendingCount, tone: "text-emerald-700", accent: "bg-emerald-500"},
          ].map((item) => (
            <article key={item.label} className="relative overflow-hidden rounded-2xl border border-blue-50 bg-white p-4 shadow-sm">
              <span className={`absolute inset-y-0 left-0 w-1 ${item.accent}`} />
              <p className="m-0 text-xs font-semibold uppercase tracking-wide text-stone-500">{item.label}</p>
              <p className={`mb-0 mt-2 text-2xl font-bold ${item.tone}`}>{item.value}</p>
            </article>
          ))}
        </section>}

        {(message || error || (!showForm && store.booksError)) && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${error || store.booksError ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
            {error || (!showForm && store.booksError) || message}
          </div>
        )}

        {!routeBookId && showForm && <section className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">{editingId ? "Edit book draft" : "New book draft"}</h2>
          <form onSubmit={(event) => event.preventDefault()} className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm font-medium">
              Title
              <input required className={inputClass} value={form.title} onChange={(event) => setForm({...form, title: event.target.value})} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">
              Author
              <input required className={inputClass} value={form.author} onChange={(event) => setForm({...form, author: event.target.value})} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">
              Price
              <input min="0" step="0.01" type="number" className={inputClass} value={form.price} onChange={(event) => setForm({...form, price: event.target.value})} />
            </label>
            <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
              Description
              <RichTextField
                value={form.description}
                minHeight={140}
                maxHeight={260}
                placeholder="Describe the book. Formatting is supported."
                onChange={(description) => setForm((current) => ({ ...current, description }))}
              />
            </div>
            <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
              Summary
              <RichTextField
                value={form.summary}
                minHeight={80}
                maxHeight={160}
                placeholder="Short listing summary"
                onChange={(summary) => setForm((current) => ({ ...current, summary }))}
              />
            </div>
            <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
              Abstract
              <RichTextField
                value={form.abstract}
                minHeight={100}
                maxHeight={220}
                onChange={(abstract) => setForm((current) => ({ ...current, abstract }))}
              />
            </div>
            <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
              Chapter one preview
              <RichTextField
                value={form.chapterOnePreview}
                minHeight={100}
                maxHeight={220}
                onChange={(chapterOnePreview) =>
                  setForm((current) => ({ ...current, chapterOnePreview }))
                }
              />
            </div>
            <label className="flex flex-col gap-1 text-sm font-medium">
              Academic level
              <select className={inputClass} value={form.academicLevel} onChange={(event) => setForm({...form, academicLevel: event.target.value})}>
                {ACADEMIC_LEVELS.map((level) => <option key={level || "none"} value={level}>{level || "Not set"}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">
              File format
              <select className={inputClass} value={form.fileFormat} onChange={(event) => setForm({...form, fileFormat: event.target.value})}>
                {FILE_FORMATS.map((format) => <option key={format || "none"} value={format}>{format || "Not set"}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
              Field of study
              <input className={inputClass} value={form.fieldOfStudy} onChange={(event) => setForm({...form, fieldOfStudy: event.target.value})} placeholder="e.g. Computer Science" />
            </label>
            <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
              What is included
              <StringListEditor
                value={form.whatIsIncluded}
                onChange={(whatIsIncluded) => setForm((current) => ({ ...current, whatIsIncluded }))}
                placeholder="e.g. Full book file"
              />
            </div>
            <div className="flex flex-col gap-1 text-sm font-medium md:col-span-2">
              Table of contents
              <TableOfContentsEditor
                value={form.tableOfContents}
                onChange={(tableOfContents) => setForm((current) => ({ ...current, tableOfContents }))}
              />
            </div>
            <UploadField
              kind="image"
              label="Cover image"
              maxSizeBytes={5 * 1024 * 1024}
              hint="JPG, PNG, WebP or GIF · maximum 5 MB"
              value={form.coverImage}
              onChange={(url) => setForm((current) => ({...current, coverImage: url}))}
            />
            <UploadField
              kind="document"
              label="Book file"
              accept=".pdf,.epub"
              value={form.fileUrl}
              onChange={(url, upload) => setForm((current) => ({
                ...current,
                fileUrl: url,
                filePublicId: upload?.publicId || upload?.public_id || "",
                fileCloudType: upload?.cloudType || upload?.cloud_type || "",
                fileDeliveryType: upload?.deliveryType || upload?.delivery_type || "",
              }))}
            />
            <div className="flex flex-wrap gap-2 md:col-span-2">
              <button
                type="button"
                disabled={store.bookMutationLoading}
                onClick={() => saveBook(false)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
              >
                {store.bookMutationLoading ? "Saving…" : "Save draft"}
              </button>
              <button
                type="button"
                disabled={store.bookMutationLoading}
                onClick={() => saveBook(true)}
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                Save and submit
              </button>
              <button type="button" onClick={resetForm} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600">
                Cancel
              </button>
            </div>
          </form>
        </section>}

        {!routeBookId && !showForm && <section className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-blue-50 px-5 py-4">
            <div>
              <h2 className="m-0 text-lg font-bold text-blue-800">Book collection</h2>
              <p className="m-0 mt-1 text-xs text-stone-500">Your uploaded titles and their review status.</p>
            </div>
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              {store.booksTotal} {store.booksTotal === 1 ? "book" : "books"}
            </span>
          </div>
          <div className="flex flex-wrap gap-3 border-b border-blue-50 bg-blue-50/40 p-4">
            <input
              type="search"
              value={search}
              onChange={(event) => {setPage(1); setSearch(event.target.value);}}
              placeholder="Search books or authors…"
              aria-label="Search books"
              className="min-w-[220px] flex-1 rounded-lg border border-blue-100 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
            />
            <select
              value={status}
              onChange={(event) => {setPage(1); setStatus(event.target.value);}}
              aria-label="Filter books by status"
              className="rounded-lg border border-blue-100 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
            >
              {STATUSES.map((value) => <option key={value || "ALL"} value={value}>{value || "All statuses"}</option>)}
            </select>
          </div>
          {store.booksLoading && !store.books.length ? (
            <div className="grid gap-3 p-5" aria-label="Loading books">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-12 animate-pulse rounded-lg bg-blue-50" />
              ))}
            </div>
          ) : store.books.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-blue-50 text-xs uppercase tracking-wide text-blue-700">
                  <tr>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Author</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Price</th>
                    <th className="px-4 py-3">Downloads</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {store.books.map((book) => (
                    <tr key={book.id || book._id} className="border-t border-blue-50 transition hover:bg-blue-50/30">
                      <td className="px-4 py-3">
                        <button type="button" className="font-semibold text-blue-800 hover:underline" onClick={() => navigate(`/resource-person/book-upload/${book.id || book._id}`)}>
                          {book.title || "Untitled"}
                        </button>
                      </td>
                    <td className="px-4 py-3 text-stone-600">{book.author || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                        {book.status || "DRAFT"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600">{book.price === null || book.price === undefined ? "—" : book.price}</td>
                    <td className="px-4 py-3 text-stone-600">{book.downloadCount ?? book.downloads ?? 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button type="button" disabled={!isEditable(book)} onClick={() => editBook(book)} className="rounded-md border border-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700 transition hover:bg-blue-50 disabled:opacity-40">Edit</button>
                        {isEditable(book) ? (
                          <button type="button" onClick={() => runAction(() => store.submitBook(book.id || book._id), "Book submitted for review.")} className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-emerald-700">Submit</button>
                        ) : String(book.status || "").toUpperCase() === "PENDING" ? (
                          <button type="button" onClick={() => runAction(() => store.unsubmitBook(book.id || book._id), "Book withdrawn from review.")} className="rounded-md bg-amber-500 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-amber-600">Withdraw</button>
                        ) : null}
                        <button
                          type="button"
                          disabled={!isEditable(book)}
                          onClick={() => confirm({
                              title: "Delete this draft book?",
                              description: "This action cannot be undone.",
                              confirmLabel: "Delete book",
                              onConfirm: async () => {
                                const bookId = book.id || book._id;
                                const result = await store.deleteBook(bookId);
                                if (!result.success) throw new Error(result.error || "Could not delete this book.");
                                setMessage("Book deleted.");
                                store.fetchBooks({page, limit: 10, ...(search ? {search} : {}), ...(status ? {status} : {})});
                              },
                            })}
                            className="rounded-md border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-40"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-5 py-10 text-center text-sm text-stone-400">
              {search || status ? "No books match these filters." : "No books yet. Create your first draft."}
            </p>
          )}
          {store.booksPages > 1 && (
            <div className="flex items-center justify-center gap-3 border-t border-blue-50 p-3 text-sm">
              <button disabled={page <= 1} onClick={() => setPage((current) => current - 1)} className="rounded-lg border border-blue-100 px-3 py-1.5 font-semibold text-blue-700 disabled:opacity-40">Previous</button>
              <span className="text-stone-500">Page {page} of {store.booksPages} · {store.booksTotal} total</span>
              <button disabled={page >= store.booksPages} onClick={() => setPage((current) => current + 1)} className="rounded-lg border border-blue-100 px-3 py-1.5 font-semibold text-blue-700 disabled:opacity-40">Next</button>
            </div>
          )}
        </section>}

        {routeBookId && (
          <section className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="m-0 text-base font-bold text-blue-800">{detail?.title || "Book details"}</h2>
            </div>
            {store.bookDetailLoading ? <p className="text-sm text-stone-400">Loading book details…</p> : store.bookDetailError ? (
              <p className="text-sm text-red-600">{store.bookDetailError}</p>
            ) : detail ? (
              <>
                <dl className="grid gap-4 sm:grid-cols-2">
                  {detail.coverImage || detail.cover_image ? (
                    <div className="sm:col-span-2">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-stone-400">Cover image</dt>
                      <dd className="mb-0 mt-2">
                        <img
                          src={detail.coverImage || detail.cover_image}
                          alt={`${detail.title || "Book"} cover`}
                          className="h-40 w-28 rounded-lg border border-blue-50 object-cover shadow-sm"
                        />
                      </dd>
                    </div>
                  ) : null}
                  {[
                    ["Author", detail.author],
                    ["Status", detail.status || "DRAFT"],
                    ["Price", detail.price === null || detail.price === undefined ? "—" : new Intl.NumberFormat("en-NG", {style: "currency", currency: "NGN"}).format(detail.price)],
                    ["Downloads", detail.downloads ?? 0],
                    ["Revenue", detail.revenue === null || detail.revenue === undefined ? "—" : new Intl.NumberFormat("en-NG", {style: "currency", currency: "NGN"}).format(detail.revenue)],
                    ["Publisher", detail.publisher?.name],
                    ["Uploaded", detail.uploadedAt],
                    ["Submitted", detail.submittedAt],
                    ["Updated", detail.updatedAt],
                    ["Moderator rejection reason", detail.rejectionReason],
                  ].map(([label, value]) => (
                    <div key={label} className={label === "Moderator rejection reason" ? "sm:col-span-2" : ""}>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-stone-400">{label}</dt>
                      <dd className="mt-1 break-words text-sm text-stone-700">
                        {value === null || value === undefined || value === "" ? "—" :
                          /uploaded|submitted|updated/i.test(label) && !Number.isNaN(Date.parse(value))
                            ? new Date(value).toLocaleString()
                            : value}
                      </dd>
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-stone-400">Description</dt>
                    <dd className="mt-1 whitespace-pre-wrap text-sm text-stone-700">{detail.description || "—"}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-stone-400">Downloadable file</dt>
                    <dd className="mt-1"><ResourceDownloadLink url={detail.fileUrl || detail.file_url} /></dd>
                  </div>
                  {detail.filePublicId || detail.file_public_id ? (
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-stone-400">File storage ID</dt>
                      <dd className="mt-1 break-all text-sm text-stone-700">{detail.filePublicId || detail.file_public_id}</dd>
                    </div>
                  ) : null}
                  {(detail.fileCloudType || detail.fileDeliveryType) ? (
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-stone-400">File delivery</dt>
                      <dd className="mt-1 text-sm text-stone-700">
                        {[detail.fileCloudType, detail.fileDeliveryType].filter(Boolean).join(" · ")}
                      </dd>
                    </div>
                  ) : null}
                </dl>

                <h3 className="mb-2 mt-6 text-sm font-bold text-blue-800">Analytics</h3>
                <ResourceAnalyticsPanel
                  analytics={store.bookAnalytics}
                  loading={store.bookAnalyticsLoading}
                  error={store.bookAnalyticsError}
                />

                <h3 className="mb-2 mt-6 text-sm font-bold text-blue-800">Downloaders ({store.bookDownloads.length})</h3>
                {store.bookDownloadsLoading ? (
                  <p className="text-sm text-stone-400">Loading downloads…</p>
                ) : store.bookDownloadsError ? (
                  <p className="text-sm text-red-600">{store.bookDownloadsError}</p>
                ) : store.bookDownloads.length === 0 ? (
                  <p className="text-sm text-stone-400">No downloads recorded yet.</p>
                ) : (
                  <ul className="divide-y divide-blue-50 rounded-lg border border-blue-50">
                    {store.bookDownloads.map((download, index) => (
                      <li key={download.id || download.userId || index} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-stone-700">
                          {[download.first_name, download.last_name].filter(Boolean).join(" ") ||
                            download.name || download.user?.name || download.email || download.user?.email ||
                            `Downloader ${index + 1}`}
                        </span>
                        <span className="text-xs text-stone-400">{download.downloaded_at || download.created_at || download.createdAt || ""}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : <p className="text-sm text-stone-400">Book details unavailable.</p>}
          </section>
        )}
      </main>
    </div>
  );
}
